/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useMemo, useState} from 'react'
import {KTSVG} from '../../../../../_metronic/helpers'
import {getConcessionariesAll} from '../../../../services/concessionariesService'
import {getUnitConsumerAllFilterPagination} from '../../../../services/unitConsumerService'
import {Loading} from '../../../components/Loadings/loading'
import {ModalAddUpdateUnitConsumer} from '../ModalAddEditUnitConsumer'

import {TableBody} from './TableBody'
import {BoardUnitConsumers, groupUnitConsumersByPlant} from './BoardUnitConsumers'
import {useAuth} from '../../../../modules/auth'
import {getPlantsAllSimple} from '../../../../services/plantsService'
import {useToast} from '../../../components/Toats'
import {ModalDeleteUnitConsumer} from '../ModalDeleteUnitConsumer'
import {PaginationTablePlants} from '../../../dashboard/TablePlants/components/Pagination'
import {SelectGlobal} from '../../../components/SelectGlobal'
import {ModalPlant} from '../../../components/modalPlant'
import {ModalEditUnitConsumerMonitoring} from '../../../concessionaries/components/ModalEditUnitConsumerMonitoring'

import listLayoutStyles from '../../../components/ListTableLayout/listTableLayout.module.css'
import {SearchInputV3} from '../../../components/SearchInputV3'
import {useQuery, keepPreviousData} from '@tanstack/react-query'

type Props = {
  className: string
}

const BOARD_PAGE_SIZE = 1000
const BOARD_PLANTS_PAGE_SIZE = 12
type ViewMode = 'table' | 'board'

const TableUnitConsumers: React.FC<Props> = ({className}) => {
  const user = useAuth()
  const {toggleToast} = useToast()

  // Estados de Modais e Seleção
  const [showCreateAppModal, setShowCreateAppModal] = useState<boolean>(false)
  const [showCreateAppModalDelete, setShowCreateAppModalDelete] = useState<boolean>(false)
  const [myUnitConsumer, setMyUnitConsumer] = useState<any | null>(null)
  const [plantModalId, setPlantModalId] = useState<string>('')
  const [concessionaireModal, setConcessionaireModal] = useState<{
    concessionaire: any
    unitConsumerId: string
  } | null>(null)

  const canEdit = !!user.currentUser?.role?.slug && user.currentUser.role.slug !== 'CLIENTE'

  const handleOpenPlant = (plantId: string) => setPlantModalId(plantId)
  const handleOpenConcessionaire = (unitConsumer: any) => {
    if (!canEdit || !unitConsumer.concessioraire) return
    setConcessionaireModal({
      concessionaire: unitConsumer.concessioraire,
      unitConsumerId: unitConsumer.id,
    })
  }

  // Estados de Filtros
  const [filterConcessionaries, setFilterConcessionaries] = useState<string>('')
  const [filterPlant, setFilterPlant] = useState<string>('')
  const [filterName, setFilterName] = useState<string>('')
  const [viewMode, setViewMode] = useState<ViewMode>('table')

  // Estado de Paginação
  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 50,
  })

  const [boardPagination, setBoardPagination] = useState<any>({
    page: 1,
    limit: BOARD_PLANTS_PAGE_SIZE,
  })

  // Evento de Resize com Debounce
  const [largura, setLargura] = useState(window.innerWidth)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    const atualizarTamanhoDaTela = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setLargura(window.innerWidth)
      }, 150)
    }

    window.addEventListener('resize', atualizarTamanhoDaTela)
    return () => {
      window.removeEventListener('resize', atualizarTamanhoDaTela)
      clearTimeout(timeoutId)
    }
  }, [])

  // QUERY PRINCIPAL
  const {
    data: queryResponse,
    isLoading: unitConsumerLoading,
    refetch: refresh,
  } = useQuery({
    queryKey: [
      'getUnitConsumers',
      filterConcessionaries,
      filterPlant,
      filterName,
      pagination.page,
      pagination.limit,
    ],
    queryFn: async () => {
      try {
        const response = await getUnitConsumerAllFilterPagination(
          filterConcessionaries || '',
          filterPlant || '',
          filterName || undefined,
          false,
          pagination.limit,
          pagination.page
        )
        return response
      } catch (error) {
        toggleToast('Erro ao buscar as unidades consumidoras!', 'error')
        throw error
      }
    },
    enabled: viewMode === 'table',
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  })

  // QUERY DA GRADE: busca tudo e pagina por usina no cliente
  const {
    data: boardResponse,
    isLoading: boardLoading,
    refetch: refreshBoard,
  } = useQuery({
    queryKey: ['getUnitConsumersBoard', filterConcessionaries, filterPlant, filterName],
    queryFn: async () => {
      try {
        return await getUnitConsumerAllFilterPagination(
          filterConcessionaries || '',
          filterPlant || '',
          filterName || undefined,
          false,
          BOARD_PAGE_SIZE,
          1
        )
      } catch (error) {
        toggleToast('Erro ao buscar as unidades consumidoras!', 'error')
        throw error
      }
    },
    enabled: viewMode === 'board',
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  })

  useEffect(() => {
    setBoardPagination((prev: any) => ({...prev, page: 1}))
  }, [filterConcessionaries, filterPlant, filterName])

  const plantGroups = useMemo(
    () => groupUnitConsumersByPlant(boardResponse?.data || []),
    [boardResponse?.data]
  )
  const boardTotalPlants = plantGroups.length
  const boardQtdPages = Math.max(1, Math.ceil(boardTotalPlants / BOARD_PLANTS_PAGE_SIZE))
  const boardCurrentPage = Math.min(boardPagination.page, boardQtdPages)
  const pagedBoardUnitConsumers = plantGroups
    .slice(
      (boardCurrentPage - 1) * BOARD_PLANTS_PAGE_SIZE,
      boardCurrentPage * BOARD_PLANTS_PAGE_SIZE
    )
    .flatMap((plant) => plant.units)

  const boardQtd = {
    start: boardTotalPlants === 0 ? 0 : (boardCurrentPage - 1) * BOARD_PLANTS_PAGE_SIZE + 1,
    end: Math.min(boardCurrentPage * BOARD_PLANTS_PAGE_SIZE, boardTotalPlants),
  }

  const boardPaginationView = {
    ...boardPagination,
    page: boardCurrentPage,
    total: boardTotalPlants,
    qtdPages: boardQtdPages,
  }

  const refreshCurrentView = () => {
    if (viewMode === 'board') {
      refreshBoard()
      return
    }
    refresh()
  }

  // Derivando os dados direto do React Query
  const unitConsumers = queryResponse?.data || []
  const totalItems = queryResponse?.total || 0
  const totalPages = queryResponse?.lastPage || 1

  // Estado Derivado para o "qtd"
  const start = (pagination.page - 1) * pagination.limit + 1
  const end = Math.min(pagination.page * pagination.limit, totalItems)
  const qtd = {start, end: end === 0 ? pagination.limit : end}

  // QUERY: Concessionárias
  const {data: concessionaries = [], isLoading: loadingConcessionaries} = useQuery({
    queryKey: ['getConcessionariesUnitConsumers'],
    queryFn: async () => {
      try {
        return await getConcessionariesAll()
      } catch (error) {
        toggleToast('Erro ao buscar a lista de concessionarias! ', 'error')
        throw error
      }
    },
    staleTime: 1000 * 60 * 60,
  })

  // QUERY: Usinas
  const {data: plants = []} = useQuery({
    queryKey: ['getPlantsUnitConsumers'],
    queryFn: async () => {
      try {
        return await getPlantsAllSimple()
      } catch (error) {
        toggleToast('Erro ao buscar a lista de usinas. ', 'error')
        throw error
      }
    },
    staleTime: 1000 * 60 * 60,
  })

  const activeFiltersCount = [filterPlant, filterConcessionaries].filter((value) => value !== '').length

  return (
    <div
      className={`card ${className}`}
      style={{
        height: '100%',
        minHeight: '60vh',
        paddingBottom: '1rem',
      }}
    >
      {/* begin::Header */}
      <div
        className={`border-0 ${listLayoutStyles.listCardHeader}`}
        style={{padding: '1.25rem 1.25rem 0'}}
      >
        <div className={`${listLayoutStyles.listToolbar} ${listLayoutStyles.listToolbarPanel}`}>
          <div className={listLayoutStyles.listToolbarFilters}>
            <SearchInputV3
              className={`form-control form-control-sm ${listLayoutStyles.listSearch}`}
              placeholder={'Buscar por nome'}
              setMySearch={setFilterName}
              setPagination={setPagination}
              style={{width: '100%'}}
            />

            <div className='dropdown'>
              <button
                type='button'
                className='btn btn-lg btn-light btn-active-color-primary btn-sm dropdown-toggle position-relative'
                data-bs-toggle='dropdown'
                aria-expanded='false'
                data-bs-auto-close='outside'
                title='Filtros'
              >
                <span>
                  <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-3' />
                </span>
                {activeFiltersCount > 0 && (
                  <span className='position-absolute top-0 start-100 translate-middle badge badge-circle badge-primary'>
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <div
                className={`dropdown-menu dropdown-menu-end p-4 ${listLayoutStyles.listFilterDropdownMenu}`}
              >
                <label
                  className='form-label fw-bold'
                  style={{marginTop: '0.5rem', marginBottom: '0.5rem'}}
                >
                  Usinas
                </label>
                <div className='separator my-2'></div>

                <div style={{marginTop: '0.5rem', marginBottom: '1rem', width: '100%'}}>
                  <SelectGlobal
                    itens={plants}
                    buttonAll={true}
                    textSearch={'Buscar usina'}
                    textButtonInit={'Filtrar por usina'}
                    labelKey='name'
                    valueKey='id'
                    typeIcon='img'
                    linkIcon='https://res.cloudinary.com/deq3ehxe3/image/upload/v1735238752/plant2_tuwshh.png'
                    sizeIconSvg='2'
                    value={filterPlant}
                    setPagination={setPagination}
                    onChange={(value: string) => setFilterPlant(value)}
                  />

                  <label
                    className='form-label fw-bold'
                    style={{marginTop: '1rem', marginBottom: '0.5rem'}}
                  >
                    Concessionárias
                  </label>
                  <div className='separator my-3'></div>

                  <SelectGlobal
                    itens={concessionaries}
                    buttonAll={true}
                    textSearch={'Buscar concessionária'}
                    textButtonInit={'Selecione a concessionária'}
                    labelKey='name'
                    valueKey='name'
                    typeIcon='badge'
                    badgeKey='status_mon_aut'
                    colorsBadge={{
                      MONITORADA: 'success',
                      'NÃO MONITORADA': 'danger',
                      'EM MANUTENÇÃO': 'info',
                    }}
                    value={filterConcessionaries}
                    className='mt-1'
                    setPagination={setPagination}
                    onChange={(value: string) => setFilterConcessionaries(value)}
                  />
                </div>

                <div className='separator my-2'></div>

                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <button
                    className='btn btn-color-gray-600 btn-active-color-primary'
                    onClick={() => {
                      setFilterPlant('')
                      setFilterConcessionaries('')
                      setPagination((prev: any) => ({...prev, page: 1}))
                    }}
                  >
                    Limpar todos os filtros{' '}
                    <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-5' />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={`card-toolbar ${listLayoutStyles.listToolbarActions}`}>
            <div className='d-flex align-items-center gap-1 me-2'>
              <button
                type='button'
                className={`btn btn-sm btn-icon ${
                  viewMode === 'table' ? 'btn-light-primary' : 'btn-light'
                }`}
                onClick={() => setViewMode('table')}
                title='Visualização em tabela'
              >
                <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-2' />
              </button>
              <button
                type='button'
                className={`btn btn-sm btn-icon ${
                  viewMode === 'board' ? 'btn-light-primary' : 'btn-light'
                }`}
                onClick={() => setViewMode('board')}
                title='Visualização em grade'
              >
                <KTSVG path='/media/icons/duotune/general/gen025.svg' className='svg-icon-2' />
              </button>
            </div>

            {user.currentUser?.role?.slug && user.currentUser.role.slug !== 'CLIENTE' && (
              <a
                onClick={(event) => {
                  event.preventDefault()
                  setShowCreateAppModal(true)
                }}
                href='#'
                className='btn btn-sm btn-light-primary'
                title='Click para adicionar uma unidade consumidora'
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Nova Unidade Consumidora
              </a>
            )}
          </div>
        </div>
      </div>

      <div
        className='card-body py-3'
        style={
          viewMode === 'table' && largura < 1200
            ? {height: '20rem', overflow: 'auto', paddingTop: '0rem'}
            : {overflow: 'auto'}
        }
      >
        {viewMode === 'board' ? (
          loadingConcessionaries || boardLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '3rem',
              }}
            >
              <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
            </div>
          ) : (
            <BoardUnitConsumers
              unitConsumers={pagedBoardUnitConsumers}
              setMyUnitConsumer={setMyUnitConsumer}
              setShowCreateAppModal={setShowCreateAppModal}
              setShowCreateAppModalDelete={setShowCreateAppModalDelete}
              onCreateUnitConsumer={() => {
                setMyUnitConsumer(null)
                setShowCreateAppModal(true)
              }}
              onOpenPlant={handleOpenPlant}
              onOpenConcessionaire={handleOpenConcessionaire}
            />
          )
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-hover table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted fs-7 text-uppercase gs-0'>
                  <th className='min-w-250px'>Denominação</th>
                  <th className='min-w-150px'>Tipo de Unidade</th>
                  <th className='min-w-250px'>Usina Vinculada</th>
                  <th className='min-w-200px'>Contrato</th>
                  <th className='min-w-250px'>Concessionária Vinculada</th>
                  {user.currentUser &&
                    user.currentUser.companies &&
                    user.currentUser.companies.length > 0 &&
                    user.currentUser.companies[0].planos === 'PREMIUM' && (
                      <th className='min-w-150px'>Monit. Autom.</th>
                    )}
                  <th className='min-w-100px text-end'>Actions</th>
                </tr>
              </thead>
              {loadingConcessionaries || unitConsumerLoading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    height: '30rem',
                    alignItems: 'center',
                  }}
                >
                  <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
                </div>
              ) : (
                <TableBody
                  unitConsumers={unitConsumers}
                  setMyUnitConsumer={setMyUnitConsumer}
                  setShowCreateAppModal={setShowCreateAppModal}
                  setShowCreateAppModalDelete={setShowCreateAppModalDelete}
                  onOpenPlant={handleOpenPlant}
                  onOpenConcessionaire={handleOpenConcessionaire}
                />
              )}
            </table>
          </div>
        )}
      </div>

      <div className='card-footer pt-0 pb-2'>
        {viewMode === 'board' ? (
          <PaginationTablePlants
            pagination={boardPaginationView}
            setPagination={setBoardPagination}
            qtd={boardQtd}
          />
        ) : (
          <PaginationTablePlants
            pagination={{...pagination, total: totalItems, qtdPages: totalPages}}
            setPagination={setPagination}
            qtd={qtd}
          />
        )}
      </div>

      <ModalAddUpdateUnitConsumer
        show={showCreateAppModal}
        id={myUnitConsumer ? myUnitConsumer.id : undefined}
        data={myUnitConsumer ? myUnitConsumer : undefined}
        handleClose={() => {
          setMyUnitConsumer(null)
          setShowCreateAppModal(false)
          refreshCurrentView()
        }}
      />
      <ModalDeleteUnitConsumer
        show={showCreateAppModalDelete}
        data={myUnitConsumer ? myUnitConsumer : undefined}
        id={myUnitConsumer ? myUnitConsumer.id : undefined}
        handleClose={() => {
          setMyUnitConsumer(null)
          setShowCreateAppModalDelete(false)
          refreshCurrentView()
        }}
      />

      {plantModalId && (
        <ModalPlant
          show={!!plantModalId}
          plant={plantModalId}
          handleClose={() => setPlantModalId('')}
        />
      )}

      <ModalEditUnitConsumerMonitoring
        show={!!concessionaireModal}
        concessionaire={concessionaireModal?.concessionaire ?? null}
        unitConsumerId={concessionaireModal?.unitConsumerId}
        handleClose={() => {
          setConcessionaireModal(null)
          refreshCurrentView()
        }}
      />
    </div>
  )
}

export {TableUnitConsumers}
